import { useState } from 'react';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Select,
  Button,
  Skeleton,
  Pagination,
  Modal,
  NumberInput,
  SimpleGrid,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconReceiptRefund, IconArrowRight } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import { useCreditMemos, useInvoices, useApplyCreditMemo } from '@/hooks/usePortalApi';
import type { CreditMemo } from '@/types';
import dayjs from 'dayjs';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'unapplied', label: 'Unapplied' },
  { value: 'partial', label: 'Partially Applied' },
  { value: 'applied', label: 'Fully Applied' },
];

const PAGE_SIZE = 20;

export default function CreditMemos() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [applyCredit, setApplyCredit] = useState<CreditMemo | null>(null);
  const [applyInvoiceId, setApplyInvoiceId] = useState<string | null>(null);
  const [applyAmount, setApplyAmount] = useState<number>(0);
  const [applyOpen, { open: openApply, close: closeApply }] = useDisclosure(false);

  const { data, isLoading } = useCreditMemos({
    status: status || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  const { data: invoiceData } = useInvoices({ status: 'open' });
  const { data: overdueInvoiceData } = useInvoices({ status: 'overdue' });
  const applyCreditMemo = useApplyCreditMemo();

  const credits = data?.items ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  const unpaidInvoices = [
    ...(invoiceData?.items ?? []),
    ...(overdueInvoiceData?.items ?? []),
  ].filter((inv) => parseFloat(inv.balance_due) > 0);

  const invoiceOptions = unpaidInvoices.map((inv) => ({
    value: String(inv.id),
    label: `${inv.invoice_number} — $${parseFloat(inv.balance_due).toFixed(2)} due`,
  }));

  const totalUnapplied = credits
    .reduce((sum, c) => sum + parseFloat(c.balance || '0'), 0);

  const handleOpenApply = (credit: CreditMemo) => {
    setApplyCredit(credit);
    setApplyAmount(parseFloat(credit.balance));
    setApplyInvoiceId(null);
    openApply();
  };

  const handleApply = () => {
    if (!applyCredit || !applyInvoiceId || applyAmount <= 0) return;
    applyCreditMemo.mutate(
      {
        credit_id: applyCredit.id,
        invoice_id: parseInt(applyInvoiceId, 10),
        amount: applyAmount,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Credit applied',
            message: `$${applyAmount.toFixed(2)} applied to invoice successfully.`,
            color: 'brand',
          });
          closeApply();
          setApplyCredit(null);
        },
        onError: () => {
          notifications.show({
            title: 'Failed to apply credit',
            message: 'Unable to apply credit memo. Please try again.',
            color: 'red',
          });
        },
      },
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader title="Credit Memos" description="View credits and apply them to invoices" />

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <StatCard
          title="Total Credits"
          value={String(data?.total ?? 0)}
          icon={<IconReceiptRefund size={24} />}
        />
        <StatCard
          title="Unapplied Balance"
          value={`$${totalUnapplied.toFixed(2)}`}
          icon={<IconReceiptRefund size={24} />}
        />
      </SimpleGrid>

      <Group justify="flex-end">
        <Select
          placeholder="Filter by status"
          data={STATUS_OPTIONS}
          value={status}
          onChange={(v) => { setStatus(v ?? ''); setPage(1); }}
          w={180}
          size="sm"
          clearable
        />
      </Group>

      {isLoading ? (
        <Stack gap="xs">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={50} radius="md" />
          ))}
        </Stack>
      ) : credits.length === 0 ? (
        <EmptyState
          icon={<IconReceiptRefund size={30} />}
          title="No credit memos"
          description={status ? 'Try a different filter' : 'No credit memos have been issued'}
        />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Credit #</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Invoice</Table.Th>
                  <Table.Th>Reason</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Amount</Table.Th>
                  <Table.Th ta="right">Applied</Table.Th>
                  <Table.Th ta="right">Balance</Table.Th>
                  <Table.Th w={100} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {credits.map((credit) => {
                  const balance = parseFloat(credit.balance);
                  return (
                    <Table.Tr key={credit.id}>
                      <Table.Td>
                        <Text size="sm" fw={500}>{credit.credit_number}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{dayjs(credit.issued_date).format('MMM D, YYYY')}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{credit.invoice_number ?? '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">{credit.reason}</Text>
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge status={credit.status} />
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="sm">${parseFloat(credit.amount).toFixed(2)}</Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="sm">${parseFloat(credit.applied_amount).toFixed(2)}</Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="sm" fw={500} c={balance > 0 ? 'brand' : 'dimmed'}>
                          ${balance.toFixed(2)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {balance > 0 && (
                          <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconArrowRight size={14} />}
                            onClick={() => handleOpenApply(credit)}
                          >
                            Apply
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && (
            <Group justify="center">
              <Pagination total={totalPages} value={page} onChange={setPage} />
            </Group>
          )}
        </>
      )}

      {/* Apply credit modal */}
      <Modal
        opened={applyOpen}
        onClose={closeApply}
        title={`Apply ${applyCredit?.credit_number ?? 'Credit'}`}
        size="md"
      >
        {applyCredit && (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Available balance: <Text span fw={600} c="brand">${parseFloat(applyCredit.balance).toFixed(2)}</Text>
            </Text>
            <Select
              label="Apply to Invoice"
              placeholder="Select an invoice"
              data={invoiceOptions}
              value={applyInvoiceId}
              onChange={setApplyInvoiceId}
              searchable
              required
            />
            <NumberInput
              label="Amount to Apply"
              min={0.01}
              max={parseFloat(applyCredit.balance)}
              step={0.01}
              decimalScale={2}
              fixedDecimalScale
              value={applyAmount}
              onChange={(v) => setApplyAmount(Number(v) || 0)}
              prefix="$"
              required
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={closeApply}>Cancel</Button>
              <Button
                onClick={handleApply}
                loading={applyCreditMemo.isPending}
                disabled={!applyInvoiceId || applyAmount <= 0}
              >
                Apply Credit
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
